import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { format } from "date-fns";
import { useForm } from "react-hook-form";
import { IPWLFiltersProps } from "../interface";

import { IClinicListResponseData } from "@/api_framework/api_modals/Clinics";
import {
  IOrgCityListResponseData,
  IOrgProviderListResponseData,
  IOrgServicesListResponseData,
} from "@/api_framework/api_modals/Org";
import {
  IPatientWaitingListFiltersParamsBody,
  IPatientWaitingStatsParamsBody,
  PWL_DEFAULT_PAGE_SIZE,
  ValidBookingStatusesNew,
} from "@/api_framework/api_modals/Patients";
import { usePatientWaitingListAPI } from "@/app/hooks/api_hooks/PWL/usePatientWaitingListAPI";
import {
  ConfigurationOptionsProvidersAPI,
  useOrgClinicProvidersAPI,
} from "@/app/hooks/api_hooks/org/useOrgClinicProvidersAPI";
import clsx from "clsx";
import { usePWLFilters } from "@/app/hooks/usePWLFilters";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { ROUTES } from "@/app/routes";

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { IBookingStatusListResponseData } from "@/api_framework/api_modals/Bookings";
import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import { usePatientWaitingStatsAPI } from "@/app/hooks/api_hooks/PWL/usePatientWaitingStatsAPI";
import { useOverBookingBdeUserListAPI } from "@/app/hooks/api_hooks/bookings/useOverBookingBdeUserListAPI";
import { useStaffClinicListAPI } from "@/app/hooks/api_hooks/clinic/useStaffClinicListAPI";
import { useAfterFirstRender } from "@/app/hooks/useAfterFirstRender";
import { useConstants } from "@/app/hooks/useConstants";
import { useLoader } from "@/app/hooks/useLoader";
import { useRouter } from "@/app/hooks/useRouter";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { setLoading } from "@/app_redux/reducers/loading_reducer";
import { IPatientWaitingListState, setPWLFilters } from "@/app_redux/reducers/slices/auth/patient_waiting_list_slice";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";
import { VMSMappedObjectConstantsType } from "@/constants";
import { useMappedConstants } from "@/app/hooks/useMappedConstants";
import { useUserRoleAPI } from "@/app/hooks/api_hooks/user/useUserRoleAPI";
import { useOrderManagementStatsAPI } from "@/app/hooks/api_hooks/orders/useOrderStatsAPI";
import { IOrderManagementStatsFilterBody } from "@/api_framework/api_modals/Orders";
import ButtonCore from "@/app/components/ui/Buttons/ButtonCore";
import { useLabReportStatsListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportStatsListAPI";
import FilterRowSkeleton from "@/app/components/ui/Skeletons/FilterRowSkeleton";
import ToggleWithIcon from "@/app/components/ui/Toggles/ToggleWithIcon";

export interface IPWLFilterFormFields {
  date: string; // yyyy-mm-dd
  city: string; // city_id
  clinic: string; // clinic_id
  status: string;
  service: Array<string>;
  provider: string;
  payments?: string;
  createdBy?: Array<string>; // created_by_id
}

const PWLFiltersComponent: React.FC<IPWLFiltersProps> = ({
  currentStat,
  withStats,
  setStatCard,
  cacheFilters,
  forSlots,
  submitButtonTitle,
}) => {
  // Instantiations --------------------------------
  const stringConstants = useStringConstants();
  const { getRouteKey } = useRouter();
  const currentDate = format(new Date(Date.now()), stringConstants.fieldDOBFormat);
  const { stats: statsMapping } = useConstants("MAPPED") as VMSMappedObjectConstantsType;
  const [initialFetched, setInitialFetched] = useState<boolean>(false);
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);
  const routeId = ROUTES.ROOT.children.PATIENT_WAITING_LIST.id;
  const [bookingStatus, setBookingStatus] = useState<string | null>(null);
  const [isTeleNudgeShow, setIsTeleNudgeShow] = useState(false);
  const { routeState: bdeRouter } = useRouteInfo(getRouteKey("SLOTS", "id"));
  const { bdeUserList } = bdeRouter.state as ISlotsState;

  const fetchStatsAPI = useOrderManagementStatsAPI();
  const fetchLabStatsAPI = useLabReportStatsListAPI();

  const formHook = useForm<IPWLFilterFormFields>({
    mode: "onChange",
    defaultValues: {
      date: currentDate,
      // createdBy: [user?.user_id],
    },
  });


  const userPermissions = useAppSelector((state) => state.auth.userPermissions);
  // RBAC: PWL_SALES_TEAM_VIEW — legacy roles: sales_ops, sales_admin, central_sales_lead
  const isBD = useFeatureAccess("PWL_SALES_TEAM_VIEW");
  // RBAC: ALL_CLINICS_MULTI_SELECT — legacy roles: super_admin, tech_admin, sales_admin
  const shouldShowAllClinicOption = useFeatureAccess("ALL_CLINICS_MULTI_SELECT");
  const userId = useAppSelector((state) => state?.auth?.userLoginInfo?.user_id);
  const { bookingStatuses } = useMappedConstants();
  const { routeState } = useRouteInfo(routeId);
  const { getCachedPWLFilters } = usePWLFilters("GET");
  const { cachePWLFilters } = usePWLFilters("SET");
  const dispatch = useAppDispatch();
  const selectMenuReducer = useSelectMenuReducer;
  const isPWLLoading = useLoader("isPWLInProgress");
  const fetchPWL = usePatientWaitingListAPI();
  const [searchParams, setSearchParams] = useSearchParams();
  const { mappedCityId, mappedClinicId } = useAppSelector((state) => state?.root.UIStates);
  const fetchPWLClinics = useStaffClinicListAPI("PATIENT_WAITING_LIST");
  const fetchPWLStats = usePatientWaitingStatsAPI();
  const fetchPWLProviders = useOrgClinicProvidersAPI("PATIENT_WAITING_LIST");
  const { org_services: services } = useAppSelector((state) => state.staticAPI);
  const fetchUserList = useOverBookingBdeUserListAPI();
  const getUserRole = useUserRoleAPI();

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const formatDate = (date) => date.toISOString().split("T")[0];

  const start_date = formatDate(yesterday);
  const end_date = formatDate(today);

  // API Hooks End --------------------------------

  const { filters, clinics, providers, cities } = routeState.state as IPatientWaitingListState;

  const serviceTypeGroups = useMemo(() => {
    const grouped = new Map<string, { id: string; name: string; serviceIds: string[] }>();
    (services ?? []).forEach((service: any) => {
      const typeId = service?.service_type_id || service?.service_id;
      const typeName = service?.service_type_name || service?.service_name;
      if (!grouped.has(typeId)) {
        grouped.set(typeId, { id: typeId, name: typeName, serviceIds: [] });
      }
      grouped.get(typeId)?.serviceIds.push(service?.service_id);
    });
    return Array.from(grouped.values());
  }, [services]);

  const mapSelectedTypeIdsToServiceIds = (selectedTypeIds: string[] = []) => {
    if (!selectedTypeIds?.length || selectedTypeIds?.[0] === "-1") {
      return (services ?? []).map((s) => s?.service_id);
    }
    const selectedSet = new Set(selectedTypeIds);
    const finalServiceIds = new Set<string>();

    serviceTypeGroups.forEach((group) => {
      if (selectedSet.has(group.id)) {
        group.serviceIds.forEach((id) => finalServiceIds.add(id));
        return;
      }

      group.serviceIds.forEach((serviceId) => {
        if (selectedSet.has(serviceId)) {
          finalServiceIds.add(serviceId);
        }
      });
    });

    return Array.from(finalServiceIds);
  };

  const normalizeSelectionToTypeIds = (selectedIds: string[] = []) => {
    if (!selectedIds?.length || selectedIds?.[0] === "-1") return selectedIds;
    const selectedSet = new Set(selectedIds);
    const typeIds = serviceTypeGroups
      .filter((group) => selectedSet.has(group.id) || group.serviceIds.some((id) => selectedSet.has(id)))
      .map((group) => group.id);
    return [...new Set(typeIds)];
  };

  // Helper Functions ------------------------

  const getReducedSelectMenuItems = (
    cities: IOrgCityListResponseData[],
    providers: IOrgProviderListResponseData[],
    clinics: IClinicListResponseData[],
    bookingStatuses: IBookingStatusListResponseData[],
    services: IOrgServicesListResponseData,
  ): {
    orgCityItems: ISelectMenuItemData[];
    orgServiceItems: ISelectMenuItemData[];
    orgProviderItems: ISelectMenuItemData[];
    orgClinicItems: ISelectMenuItemData[];
    orgBookingStatusItems: ISelectMenuItemData[];
  } => {
    /* If the clinics and cities are selected with all don't show all the providers */
    const shouldShowAllProviderOption =
      (formHook?.getValues("city") === "-1" && formHook?.getValues("clinic") === "-1") || providers?.length === 0
        ? false
        : true;

    return {
      orgCityItems: selectMenuReducer(cities, "name", "id", {
        withAllSelectOption: true,
        allSelectItemOptions: {
          title: "All Cities",
        },
      }),

      orgBookingStatusItems: selectMenuReducer(bookingStatuses, "title", "id", {
        withAllSelectOption: true,
        allSelectItemOptions: {
          title: "All Appointments",
        },
      }),
      orgClinicItems: selectMenuReducer(clinics, "display_name", "id", {
        withAllSelectOption: shouldShowAllClinicOption,
        allSelectItemOptions: {
          title: "All Clinics",
        },
      }),

      orgProviderItems: selectMenuReducer(providers?.length > 0 ? providers : [], "name", "staff_id", {
        withAllSelectOption: shouldShowAllProviderOption,
      }),
      orgServiceItems: selectMenuReducer(serviceTypeGroups, "name", "id"),
    };
  };

  const bdeUserListItem = useSelectMenuReducer(bdeUserList, "name", "user_id", {
    withAllSelectOption: true,
    allSelectItemOptions: {
      title: "App Booking",
    },
  });

  const { orgBookingStatusItems, orgCityItems, orgClinicItems, orgProviderItems, orgServiceItems } =
    getReducedSelectMenuItems(
      cities,
      providers,
      clinics,
      Array.isArray(bookingStatuses) ? bookingStatuses : Object.values(bookingStatuses),
      services,
    );

  const isFiltersSelectMenuLoaded = () => {
    return (
      orgBookingStatusItems?.length > 0 &&
      orgCityItems?.length > 0 &&
      orgClinicItems?.length > 0 &&
      // orgProviderItems?.length > 0 &&
      orgServiceItems?.length > 0
    );
  };

  const constructIntialFormFields = (
    cityItems: ISelectMenuItemData[],
    clinicItems: ISelectMenuItemData[],
  ): IPWLFilterFormFields => {
    let formFields: IPWLFilterFormFields = {
      date: currentDate,
      city: mappedCityId ?? "-1", // -1 is initial state
      clinic: mappedClinicId ?? "-1",
      provider: "-1",
      service: ["-1"],
      status: "-1",
    };

    const cachedFormFilters = getCachedPWLFilters();
    if (cachedFormFilters) {
      formFields = { ...cachedFormFilters.formDump, createdBy: null };
      return formFields;
    }

    formFields = {
      ...formFields,
      city: mappedCityId, // -1 is initial state
      clinic: mappedClinicId,
      createdBy: null,
    };

    return formFields;
  };

  const constructPWLFetchBody = (formFields: IPWLFilterFormFields): IPatientWaitingListFiltersParamsBody => {
    const bookingStatusFieldValue =
      formFields.status === "All Appointments" || formFields.status === "-1"
        ? (["Upcoming", "Waiting", "Attending", "Completed", "Cancelled"] as ValidBookingStatusesNew[])
        : ([formFields.status] as ValidBookingStatusesNew[]);

    const PWLBody: IPatientWaitingListFiltersParamsBody = {
      booking_statuses: bookingStatusFieldValue,
      clinic_ids: formFields.clinic === "-1" ? clinics.map((c) => c.id) : [formFields.clinic],
      date: formFields.date,
      service_ids: mapSelectedTypeIdsToServiceIds(formFields.service),
      staff_ids: formFields?.provider === "-1" ? providers?.map((p) => p.staff_id) : [formFields?.provider],
      created_by:
        bdeUserList?.length < formFields?.createdBy?.length
          ? ["1"]
          : formFields.createdBy?.length > 0
            ? formFields.createdBy
            : ["1"],
    };
    return PWLBody;
  };

  const fetcOrderStats = () => {
    const statsBody: IOrderManagementStatsFilterBody = {
      start_date: start_date,
      end_date: end_date,
      clinic_id: mappedClinicId?.split(","),
    };
    fetchStatsAPI(statsBody);
  };

  const fetchLabStats = () => {
    const statsBody = {
      start_date: start_date,
      end_date: end_date,
      clinic_ids: mappedClinicId?.split(","),
      type: 3,
    };
    fetchLabStatsAPI(statsBody as any);
  };

  const constructPWLStatsFetchBody = (formFields: IPWLFilterFormFields): IPatientWaitingStatsParamsBody => {
    const PWLBody = constructPWLFetchBody(formFields);
    const PWLStatsBody: IPatientWaitingStatsParamsBody = {
      clinic_ids: PWLBody.clinic_ids,
      date: PWLBody.date,
      service_ids: PWLBody.service_ids,
      staff_ids: PWLBody.staff_ids,
      created_by: PWLBody?.created_by,
    };
    return PWLStatsBody;
  };

  const fetchPatientWaitingList = async (
    formFields: IPWLFilterFormFields,
    { restoreFromUrl = false }: { restoreFromUrl?: boolean } = {},
  ): Promise<void> => {
    const limit = Number(searchParams.get("limit")) || PWL_DEFAULT_PAGE_SIZE;
    const page = restoreFromUrl ? Math.max(Math.trunc(Number(searchParams.get("page"))) || 1, 1) : 1;

    if (!restoreFromUrl) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("page", "1");
      nextParams.set("limit", String(limit));
      setSearchParams(nextParams, { replace: true });
    }

    const PWLBody: IPatientWaitingListFiltersParamsBody = {
      ...constructPWLFetchBody(formFields),
      page,
      limit,
    };
    dispatch(
      setLoading({
        key: "isPWLInProgress",
        status: true,
      }),
    );

    await fetchPWL(PWLBody);
  };

  const fetchPWLStatsList = async (formFields: IPWLFilterFormFields): Promise<void> => {
    const PWLStatsBody = constructPWLStatsFetchBody(formFields);
    await fetchPWLStats(PWLStatsBody);
  };

  useEffect(() => {
    if (cities?.length > 0 && clinics?.length > 0 && services?.length > 0 && providers?.length > 0 && !initialFetched) {
      const f = constructIntialFormFields(orgCityItems, orgClinicItems);

      if (filters?.clinic_ids?.length === clinics?.length) {
        formHook.setValue("clinic", filters?.clinic_ids?.length === 1 ? filters?.clinic_ids?.join() : "-1");
      } else {
        formHook.setValue("clinic", f.clinic);
      }
      formHook.setValue("city", f.city);
      formHook.setValue("date", f.date);

      formHook.setValue("provider", f.provider);
      formHook.setValue(
        "service",
        f.service[0] === "-1" ? serviceTypeGroups.map((group) => group.id) : normalizeSelectionToTypeIds(f.service),
      );
      formHook.setValue("status", f.status);
      const today = new Date();
      const formattedDate = today?.toISOString()?.split("T")[0];

      formHook.setValue("date", formattedDate);

      // Fetch Initial PWL
      fetchPWLStatsList(f).then(async () => {
        if (withStats && withStats === true) {
          fetchPatientWaitingList(f, { restoreFromUrl: true });
          formHook.setValue("clinic", f.clinic);

        }
        const PWLFilterBody = constructPWLFetchBody(f);

        dispatch(
          setPWLFilters({
            filters: { ...PWLFilterBody, created_by: null },
          }),
        );
      });
      fetcOrderStats();
      fetchLabStats();
      setInitialFetched(true);
    }
  }, [cities, providers, clinics, services, serviceTypeGroups]);

  const onSubmit = async (data: IPWLFilterFormFields) => {
    setIsTeleNudgeShow(true);

    if (data) {
      const PWLBody = constructPWLFetchBody(data);
      try {
        if (cacheFilters && cacheFilters === true) {
          cachePWLFilters({
            ...PWLBody,
            booking_statuses: ["Upcoming", "Waiting", "Attending", "Completed", "Cancelled"] as ValidBookingStatusesNew[],
            formDump: data,
          });
        }
        formHook.setValue("status", data.status);
        dispatch(
          setPWLFilters({
            filters: { ...PWLBody, created_by: null },
          }),
        );
      } catch {
        // Handle Error
      }

      await fetchPatientWaitingList(data).then(() => {
        if (withStats && withStats === true) {
          fetchPWLStatsList(data);
          formHook.setValue("status", data.status);
        }
      });
      fetcOrderStats();
      fetchLabStats();
      if (typeof window !== "undefined" && window.innerWidth < 1024) {
        setShowMobileFilters(false);
      }
    }
  };

  useEffect(() => {
    // Set interval to fetch every 3 minutes
    const intervalId = setInterval(() => {
      fetcOrderStats();
      fetchLabStats();
    }, 5 * 60 * 1000);

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    fetchUserList();
    getUserRole();
  }, []);

  useEffect(() => {
    const formFields = formHook.getValues();
    const clinicIds = formFields.clinic === "-1" ? clinics.map((c) => c.id) : [formFields.clinic];
    const serviceIds = mapSelectedTypeIdsToServiceIds(formFields.service);
    if (clinicIds?.length > 0 && formFields.date) {
      fetchUserList({
        clinic_ids: clinicIds,
        selected_date: formFields.date,
        service_id: serviceIds,
      });
    }
  }, [formHook.watch("clinic"), formHook.watch("date"), formHook.watch("service")]);

  // Debounce function implementation

  useEffect(() => {
    const formFields: IPWLFilterFormFields = formHook.getValues();

    if (formFields && formFields.city !== "" && initialFetched) {
      if (formFields.city === "-1") {
        fetchPWLClinics({
          cityIds: cities.map((c) => c.id),
          withProviders: true,
          ROTADate: formFields?.date,
        });
      } else {
        fetchPWLClinics({
          cityIds: [formFields.city],
          withProviders: true,
          ROTADate: formFields?.date,
        });
      }
    }
  }, [formHook.watch("city")]);

  useEffect(() => {
    const formFields: IPWLFilterFormFields = formHook.getValues();
    if (formFields.clinic !== "" && formHook.watch("service")?.length > 0) {
      const serviceIds = mapSelectedTypeIdsToServiceIds(formFields.service);
      let fetchProviderConfig: ConfigurationOptionsProvidersAPI = {
        ROTADate: formFields?.date,
        serviceId: serviceIds,
      };
      if (formFields.clinic === "-1") {
        fetchProviderConfig = { ...fetchProviderConfig, clinicIds: clinics.map((c) => c.id) };
        formHook.setValue("provider", "-1");
      } else {
        fetchProviderConfig = {
          ...fetchProviderConfig,
          clinicIds: formFields?.clinic ? [formFields?.clinic] : [mappedClinicId],
        };
        formHook.setValue("provider", "-1");
      }
      fetchPWLProviders(fetchProviderConfig);
    }
  }, [formHook.watch("clinic"), formHook.watch("date"), formHook.watch("service"), mappedClinicId]);

  useAfterFirstRender(() => {
    if (!currentStat) return;
    if (filters && Object.keys(filters)?.length > 0) {
      const status = Object.keys(statsMapping)?.find((key) => statsMapping[key] === currentStat);

      let PWLFilterBody: IPatientWaitingListFiltersParamsBody = {
        ...filters,
      };

      if (currentStat?.toLowerCase() === statsMapping[0]?.toLowerCase()) {
        PWLFilterBody = {
          ...PWLFilterBody,
          booking_statuses: ["Upcoming", "Waiting", "Attending", "Cancelled", "Completed"] as ValidBookingStatusesNew[],
        };
        formHook.setValue("status", "-1"); // -1 for select menu
      } else if (status !== undefined) {
        PWLFilterBody = {
          ...PWLFilterBody,
          booking_statuses: [currentStat] as ValidBookingStatusesNew[],
        };
        formHook.setValue("status", statsMapping[status]);
      } else {
        PWLFilterBody = {
          ...PWLFilterBody,
          booking_statuses: [currentStat] as unknown as ValidBookingStatusesNew[],
        };
        formHook.setValue("status", "-1");
      }

      if (initialFetched) {
        fetchPWLStats(PWLFilterBody);
        fetchPWL(PWLFilterBody).then(() => {
          dispatch(
            setPWLFilters({
              filters: { ...PWLFilterBody, created_by: null },
            }),
          );
        });
      }
    }
  }, [currentStat]);

  return isFiltersSelectMenuLoaded() ? (
    <>
      <div className="lg:hidden">
        <div className="flex items-center justify-end gap-4 rounded-[10px] bg-white px-3">
          <p className="text-[0.875rem] font-semibold text-[#193A6A]">Filters</p>
          <ToggleWithIcon
            toggleState={showMobileFilters}
            onToggleChange={(status) => {
              setShowMobileFilters(status);
            }}
          />
        </div>
      </div>
      <form
        onSubmit={formHook.handleSubmit(onSubmit)}
        className={clsx(
          "flex flex-wrap lg:flex-nowrap w-full justify-between items-center gap-3 py-1",
          !showMobileFilters && "hidden lg:flex",
        )}
      >
        <PWLCalendarComponent
          source="pwl"
          // showFutureYears
          allowPastDaySelection={true}
          errorMessages={[{ message: "Date is required", type: "required" }]}
          register={formHook.register("date", {
            required: true,
          })}
          showTypedErrors={false}
          showLeftNav={true}
          fieldError={formHook.formState.errors.date}
          onSelectDate={(date) => {
            formHook.setValue("date", format(date, "yyyy-MM-dd"));
          }}
          defaultSelectedDate={formHook.watch("date")}
          className="!text-center "
          containerClassName="w-full max-w-[150px]"
        />

        <SearchableSelectMenu
          source="pwl"
          errorMessages={[{ message: "City is required", type: "required" }]}
          onSelectItem={(item) => {
            if (item) {
              formHook.setValue("city", item.id);
            }
          }}
          fieldError={formHook.formState.errors.city}
          register={formHook.register("city", { required: true })}
          selectItems={orgCityItems}
          placeholder="Select City"
          showTooltips={false}
          showTypedErrors={false}
          showDropdownIcon={true}
          defaultSelected={orgCityItems.filter((oc) => oc.id === formHook.watch("city"))[0]}
          className="text-gray-800"
          containerClassName="w-full"
        />

        <SearchableSelectMenu
          source="pwl"
          errorMessages={[{ message: "Clinic is required", type: "required" }]}
          onSelectItem={(item) => {
            if (item) {
              formHook.setValue("clinic", item.id);
            }
          }}
          fieldError={formHook.formState.errors.city}
          register={formHook.register("clinic", { required: true })}
          selectItems={orgClinicItems}
          placeholder="Select Clinic"
          showTooltips={false}
          showTypedErrors={false}
          showDropdownIcon={true}
          defaultSelected={orgClinicItems.filter((oc) => oc.id === formHook.watch("clinic"))[0]}
          listBoxClassName="w-full sm:w-60"
          className="text-gray-800 "
          containerClassName="w-full "
        />

        {forSlots && forSlots === true ? (
          <></>
        ) : (
          <SearchableSelectMenu
            source="pwl"
            errorMessages={[{ message: "Status is required", type: "required" }]}
            showSearchInputInDropdown={false}
            disableSearch={true}
            onSelectItem={(item) => {
              if (item) {
                formHook.setValue("status", item.title);
                setStatCard(item.title === "All Appointments" ? "Total" : item.title);
              }
            }}
            fieldError={formHook.formState.errors.status}
            register={formHook.register("status", {
              required: true,
            })}
            showTypedErrors={false}
            selectItems={orgBookingStatusItems}
            placeholder="Select Appointment Status"
            showTooltips={false}
            showDropdownIcon={true}
            defaultSelected={
              orgBookingStatusItems.filter((ob) => {
                if (formHook.watch("status") === "-1") {
                  return { id: "-1", title: "All Appointments" };
                } else {
                  return ob.title === formHook.watch("status");
                }
              })[0]
            }
            containerClassName="w-full"
            style={{
              fontSize: "2px",
            }}
            className="text-gray-800"
            listBoxClassName="w-full sm:w-64"
          />
        )}

        <SearchableMultiSelectMenu
          source="pwl"
          onSelectItem={(items) => {
            const ServiceId = items.map((i) => i.id);
            formHook.setValue("service", ServiceId);
          }}
          errorMessages={[{ message: "Required", type: "required" }]}
          fieldError={formHook.formState.errors.service}
          register={formHook.register("service", {
            required: true,
          })}
          withSelectAll
          defaultSelectAll
          withDropDownSearch
          showTooltips={true}
          showTypedErrors={false}
          containerClassName=" w-full"
          placeholder="Select Service"
          defaultSelected={orgServiceItems.filter((c) => formHook.watch("service")?.includes(c.id))}
          selectItems={orgServiceItems}
        />
        <SearchableSelectMenu
          source="pwl"
          errorMessages={[{ message: "Provider is required", type: "required" }]}
          onSelectItem={(item) => {
            if (item) {
              formHook.setValue("provider", item.id);
            }
          }}
          fieldError={formHook.formState.errors.provider}
          register={formHook.register("provider", {
            required: true,
          })}
          showTypedErrors={false}
          selectItems={orgProviderItems}
          placeholder="Select Provider"
          showTooltips={true}
          showDropdownIcon={true}
          defaultSelected={orgProviderItems.filter((op) => op.id === formHook.watch("provider"))[0]}
          itemsClassName="text-xs"
          listBoxClassName="w-full"
          className="text-gray-800"
          containerClassName="w-full"
          showDropdownWhenNoData
          dropdownNoDataContent={
            <div className="text-sm">
              <p className="text-gray-800">No Providers</p>
              <p className="text-gray-500">update ROTA for staffs</p>
            </div>
          }
        />
        {isBD &&
          <SearchableMultiSelectMenu
            source="pwl"
            onSelectItem={(items) => {
              const createdById = items.map((i) => i.id);
              formHook.setValue("createdBy", createdById);
            }}
            errorMessages={[{ message: "Required", type: "required" }]}
            fieldError={formHook.formState.errors.createdBy}
            register={formHook.register("createdBy", {
              required: false,
            })}
            withSelectAll
            withDropDownSearch
            showTooltips={true}
            showTypedErrors={false}
            containerClassName="w-full"
            placeholder="Select User"
            defaultSelected={bdeUserListItem.filter((c) => formHook.watch("createdBy")?.includes(c.id))}
            selectItems={bdeUserListItem}
          />
        }
        <div className="flex items-center">
          <ButtonCore
            size="PWL"
            type="primary"
            asType="submit"
            asSubmitText={submitButtonTitle && submitButtonTitle !== "" ? submitButtonTitle : "Apply Filters"}
            disabled={isPWLLoading}
            className={` cursor-pointer ${isPWLLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
          />
        </div>
      </form>
    </>
  ) : (
    <FilterRowSkeleton fieldsCount={6} />
  );
};

export default PWLFiltersComponent;
